import { useMemo } from "react";
import { type ColumnDef } from "@tanstack/react-table";

import { type AuditEventResponse } from "@vega-api/generated/types/auditEventResponse";
import { Avatar } from "@vega-core/components/Avatar/Avatar";
import { HtmlContent } from "@vega-core/components/HtmlContent/HtmlContent";
import { m } from "@vega-core/paraglide/messages";
import {
  DateFormatString,
  formatDateByPattern,
} from "@vega-core/utils/formatDate";
import { getInitials } from "@vega-core/utils/getInitials";

export const useAuditTableColumns = () => {
  const columns: ColumnDef<AuditEventResponse>[] = useMemo(
    () => [
      {
        accessorKey: "userName",
        header: m.coInvestments_audit_user(),
        cell: ({ row }) => {
          const { userName, userEmail } = row.original;

          return (
            <div className="flex items-center gap-3">
              <Avatar
                className="w-6 h-6"
                initials={getInitials(userName)}
                variant="primary"
              />
              <div className="flex flex-col">
                <span className="body-base-medium text-text/primary">
                  {userName}
                </span>
                <span className="body-small-regular text-text/secondary">
                  {userEmail}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "eventDescription",
        header: m.coInvestments_audit_event(),
        cell: ({ getValue }) => (
          <div className="body-base-regular text-text/primary">
            <HtmlContent content={getValue() as string} />
          </div>
        ),
      },
      {
        accessorKey: "eventTimestamp",
        header: m.coInvestments_audit_date(),
        cell: ({ getValue }) => {
          const timestamp = getValue() as string;

          return (
            <span className="body-base-regular text-text/primary">
              {formatDateByPattern(
                timestamp,
                DateFormatString.US_DATE_TIME_DOT,
              )}
            </span>
          );
        },
      },
    ],
    [],
  );

  return { columns };
};
